import { useCallback, useContext } from "react";
import { useParams } from "react-router-dom";
import { WebsiteContext } from "@/pages/website/WebsiteProvider";
import { ICheckout } from "@/pages/checkout/hooks/useCheckoutSteps";
import { useUpdateOrderMutation } from "@/features/organizations/api/ordersApi";
import { Order, OrderStatus } from "@/types";
import { useAppSelector } from "@/hooks";
import useRoutes from "@/hooks/useRoutes";
import pick from "lodash/pick";
import isEmpty from "lodash/isEmpty";
import { contactFormFields } from "@/pages/checkout/components/ContactForm";
import { locationFormFields } from "@/pages/checkout/components/LocationForm";
import useSendThroughWhatsapp from "@/pages/checkout/hooks/useSendThroughWhatsapp";

const useSendOrder = () => {
  const [navigate] = useRoutes();
  const { orderId } = useParams();
  const [updateOrder] = useUpdateOrderMutation();
  const { organization } = useContext(WebsiteContext);
  const activeOrder = useAppSelector((store) => store.order);
  const sendThroughWhatsapp = useSendThroughWhatsapp();

  const sendOrder = useCallback(
    async (values: ICheckout) => {
      if (!organization) return;

      let order: Order = { ...activeOrder };

      // add payment details
      if (values.paymentMethod) {
        order = {
          ...order,
          paymentDetails: {
            method: values.paymentMethod,
          },
        };
      }

      // add contact details
      const contactDetails = pick(values, contactFormFields) as Order["client"];

      if (!isEmpty(contactDetails)) {
        order = {
          ...order,
          client: contactDetails,
        };
      }

      // add location location
      const locationDetails = pick(values, locationFormFields) as Order["deliveryLocation"];

      if (!isEmpty(locationDetails)) {
        order = {
          ...order,
          deliveryLocation: locationDetails,
        };
      }

      // check org preferences
      if (organization.settings?.orderManagement === "whatsapp") {
        await sendThroughWhatsapp({
          ...order,
          status: OrderStatus.REVIEWING,
        });
      } else if (orderId) {
        await updateOrder({
          organizationId: organization.id,
          order: {
            ...order,
            id: orderId,
            status: OrderStatus.REVIEWING,
          },
        });

        navigate("organizations.order.status", { orderId });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orderId, organization]
  );

  return sendOrder;
};

export default useSendOrder;
